import React from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import {
  useThemeMode,
  systemMode,
} from "../../../_metronic/partials/layout/theme-mode/ThemeModeProvider";

type ModernRevenueChartProps = {
  // New: pre-computed values (Admin Dashboard)
  revenueByBranch?: { branch: string; customerRevenue: number; dealerCost: number }[];
  // Old: raw data array (Finance Dashboard)
  jobs?: any[];
  loading?: boolean;
};

export const ModernRevenueChart: React.FC<ModernRevenueChartProps> = ({
  revenueByBranch,
  jobs,
  loading,
}) => {
  const { mode } = useThemeMode();
  const isDarkMode =
    mode === "dark" || (mode === "system" && systemMode === "dark");

  // Compute from raw data if pre-computed values not provided
  let data: { branch: string; customerRevenue: number; dealerCost: number }[] = [];
  if (revenueByBranch) {
    data = revenueByBranch;
  } else if (jobs) {
    const branchData: Record<string, { customerRevenue: number; dealerCost: number }> = {};
    for (const job of jobs) {
      const branch = job.branchName || 'Unknown';
      if (!branchData[branch]) branchData[branch] = { customerRevenue: 0, dealerCost: 0 };
      for (const t of (job.selectedTreatments || [])) {
        branchData[branch].customerRevenue += (t.customerPrice || 0);
        branchData[branch].dealerCost += (t.dealerPrice || 0);
      }
    }
    data = Object.entries(branchData)
      .map(([branch, vals]) => ({ branch, ...vals }))
      .sort((a, b) => b.customerRevenue - a.customerRevenue);
  }

  const categories = data.map(d => d.branch);
  const customerRevenueData = data.map(d => d.customerRevenue);
  const dealerCostData = data.map(d => d.dealerCost);

  // Calculate totals for summary
  const totalCustomerRevenue = customerRevenueData.reduce(
    (sum, val) => sum + val,
    0
  );
  const totalDealerCost = dealerCostData.reduce((sum, val) => sum + val, 0);
  const totalMargin = totalCustomerRevenue - totalDealerCost;
  const marginPercentage =
    totalCustomerRevenue > 0
      ? ((totalMargin / totalCustomerRevenue) * 100).toFixed(1)
      : "0";

  const chartOptions: ApexOptions = {
    chart: {
      type: "bar",
      fontFamily: "inherit",
      toolbar: {
        show: false,
      },
      stacked: false,
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        horizontal: true,
        barHeight: "65%",
        dataLabels: {
          position: "top",
        },
      },
    },
    colors: ["#50cd89", "#f1416c"], // Customer (green), Dealer (red)
    dataLabels: {
      enabled: true,
      textAnchor: "start",
      style: {
        colors: ["#fff"],
        fontSize: "11px",
        fontWeight: 600,
      },
      formatter: function (val: number) {
        if (val === 0) return "";
        return "₹" + val.toLocaleString();
      },
      offsetX: 8,
    },
    xaxis: {
      categories: categories,
      labels: {
        style: {
          colors: "#a1a5b7",
          fontSize: "12px",
        },
        formatter: function (val: string) {
          return "₹" + Number(val).toLocaleString();
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: isDarkMode ? "#ffffff" : "#181c32",
          fontSize: "13px",
          fontWeight: 600,
        },
        maxWidth: 180,
      },
    },
    grid: {
      show: true,
      borderColor: isDarkMode ? "#2b2b40" : "#eff2f5",
      strokeDashArray: 4,
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: false,
        },
      },
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 10,
      },
    },
    tooltip: {
      theme: isDarkMode ? "dark" : "light",
      shared: true,
      intersect: false,
      y: {
        formatter: function (val: number) {
          return "₹" + val.toLocaleString();
        },
      },
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const customerValue = series[0][dataPointIndex];
        const dealerValue = series[1][dataPointIndex];
        const margin = customerValue - dealerValue;
        const marginPct =
          customerValue > 0 ? ((margin / customerValue) * 100).toFixed(1) : "0";

        return `<div class="apexcharts-tooltip-custom" style="padding: 12px; background: ${
          isDarkMode ? "#1e1e2d" : "#fff"
        }; border: 1px solid ${isDarkMode ? "#2b2b40" : "#e4e6ef"};">
                    <div style="font-weight: 600; margin-bottom: 8px; color: ${
                      isDarkMode ? "#fff" : "#181c32"
                    };">${w.globals.labels[dataPointIndex]}</div>
                    <div style="display: flex; align-items: center; margin-bottom: 4px;">
                        <span style="width: 10px; height: 10px; background: #50cd89; border-radius: 2px; margin-right: 6px;"></span>
                        <span style="color: ${
                          isDarkMode ? "#a1a5b7" : "#5e6278"
                        };">Customer Revenue:</span>
                        <span style="margin-left: auto; font-weight: 600; color: ${
                          isDarkMode ? "#fff" : "#181c32"
                        };">₹${customerValue.toLocaleString()}</span>
                    </div>
                    <div style="display: flex; align-items: center; margin-bottom: 4px;">
                        <span style="width: 10px; height: 10px; background: #f1416c; border-radius: 2px; margin-right: 6px;"></span>
                        <span style="color: ${
                          isDarkMode ? "#a1a5b7" : "#5e6278"
                        };">Dealer Cost:</span>
                        <span style="margin-left: auto; font-weight: 600; color: ${
                          isDarkMode ? "#fff" : "#181c32"
                        };">₹${dealerValue.toLocaleString()}</span>
                    </div>
                    <div style="border-top: 1px solid ${
                      isDarkMode ? "#2b2b40" : "#e4e6ef"
                    }; margin-top: 6px; padding-top: 6px;">
                        <div style="display: flex; align-items: center;">
                            <span style="color: ${
                              isDarkMode ? "#a1a5b7" : "#5e6278"
                            };">Margin:</span>
                            <span style="margin-left: auto; font-weight: 700; color: ${
                              margin >= 0 ? "#50cd89" : "#f1416c"
                            };">₹${margin.toLocaleString()} (${marginPct}%)</span>
                        </div>
                    </div>
                </div>`;
      },
    },
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "right",
      fontSize: "13px",
      fontWeight: 600,
      labels: {
        colors: isDarkMode ? "#ffffff" : "#181c32",
      },
      markers: {
        size: 6,
        shape: "square",
      },
      itemMargin: {
        horizontal: 10,
        vertical: 0,
      },
    },
  };

  return (
    <div
      className="card h-100"
      style={{ backgroundColor: isDarkMode ? "#1e1e2d" : "" }}
    >
      <div className="card-header border-0 pt-5 pb-3">
        <h3 className="card-title align-items-start flex-column">
          <span
            className={`card-label fw-bolder fs-3 ${
              isDarkMode ? "text-white" : "text-dark"
            }`}
          >
            Revenue Pipeline
          </span>
          <span className="text-muted mt-1 fw-bold fs-7">
            Top branches by active job value
          </span>
        </h3>
      </div>

      {/* Summary Stats */}
      {!loading && customerRevenueData.length > 0 && (
        <div className="card-body pt-0 pb-2">
          <div className="row g-3 mb-4">
            <div className="col-6">
              <div
                className="p-3 rounded"
                style={{
                  backgroundColor: isDarkMode ? "#1b1b29" : "#e8fff3",
                  border: isDarkMode ? "1px solid #2b2b40" : "none",
                }}
              >
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <span className="text-muted fw-semibold d-block fs-8">
                      Customer Revenue
                    </span>
                    <span className="fw-bold fs-6 text-success">
                      ₹{totalCustomerRevenue.toLocaleString()}
                    </span>
                  </div>
                  <div className="w-35px h-35px rounded d-flex align-items-center justify-content-center bg-success">
                    <i className="bi bi-arrow-up-right fs-4 text-white"></i>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-6">
              <div
                className="p-3 rounded"
                style={{
                  backgroundColor: isDarkMode ? "#1b1b29" : "#fff5f8",
                  border: isDarkMode ? "1px solid #2b2b40" : "none",
                }}
              >
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <span className="text-muted fw-semibold d-block fs-8">
                      Dealer Cost
                    </span>
                    <span className="fw-bold fs-6 text-danger">
                      ₹{totalDealerCost.toLocaleString()}
                    </span>
                  </div>
                  <div className="w-35px h-35px rounded d-flex align-items-center justify-content-center bg-danger">
                    <i className="bi bi-arrow-down-right fs-4 text-white"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="card-body pt-0 pb-3">
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "300px" }}
          >
            <span className="spinner-border text-primary"></span>
          </div>
        ) : customerRevenueData.length === 0 ? (
          <div className="d-flex justify-content-center align-items-center h-200px text-gray-400 fw-bold">
            No revenue data available
          </div>
        ) : (
          <div id="revenue_chart">
            <Chart
              options={chartOptions}
              series={[
                { name: "Customer Revenue", data: customerRevenueData },
                { name: "Dealer Cost", data: dealerCostData },
              ]}
              type="bar"
              height={300}
            />
          </div>
        )}
      </div>
    </div>
  );
};
